import { HTMLAttributes, useEffect } from 'react';

import { SidePanel } from '@/components/modals/SidePanel';
import { Loader } from '@/components/progress/Loader/Loader';
import { useModalState } from '@/hooks/useModalState';
import { AIOnboardingSuggestionDrawer } from '@/modules/aiSuggestions/components/AIOnboardingSuggestionDrawer';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';

import { useFamilyTreeContext } from '../../contexts';
import { useFamilyTreeRelationships } from '../../hooks/useFamilyTreeRelationships';
import { LinkedPanel } from './components/LinkedPanel';
import { SidePanelFooter } from './components/SidePanelFooter';
import { UnlinkedPanel } from './components/UnlinkedPanel';

export interface FamilyTreeSidePanelProps
  extends HTMLAttributes<HTMLDivElement> {
  householdId: string;
}

export function FamilyTreeSidePanel({ householdId }: FamilyTreeSidePanelProps) {
  const { state, dispatch } = useFamilyTreeContext();

  const { sidePanelVariant } = state;

  const { selectedPerson, isLoading } = useFamilyTreeRelationships();

  // A person is citable when it has an onboarding suggestion; clicking the
  // magnifying glass opens that suggestion's source document. Mirrors the
  // People list behavior (ClientPeopleListView).
  const suggestionId = selectedPerson?.onboardedIndividualSuggestion?.[0]?.id;

  const [suggestionDrawerState, suggestionDrawerModal] = useModalState<{
    suggestionId: string;
  }>();

  // Close the source document drawer whenever the selected person changes so it
  // can never show a stale person's document while the header reflects a new
  // selection (e.g. clicking another node or a relationship card).
  const { closeModal: closeSuggestionDrawer } = suggestionDrawerModal;
  useEffect(() => {
    closeSuggestionDrawer();
  }, [state.selectedPersonId, closeSuggestionDrawer]);

  const handleClose = () => {
    dispatch({ type: 'CLOSE_SIDE_PANEL' });
  };

  const handleBackToUnlinked = () => {
    dispatch({
      type: 'OPEN_SIDE_PANEL',
      personId: '',
      variant: 'unlinked',
      history: undefined,
    });
  };

  if (isLoading) {
    return (
      <SidePanel.Panel>
        <SidePanel.Content>
          <Loader
            boxProps={{
              display: 'flex',
              margin: 'auto',
            }}
            circularProgressProps={{ size: 40 }}
          />
        </SidePanel.Content>
      </SidePanel.Panel>
    );
  }

  if (!selectedPerson && sidePanelVariant === 'linked') {
    return null;
  }

  return (
    <SidePanel.Panel
      data-testid="FamilyTreeSidePanel"
      key={selectedPerson?.id ?? 'unlinked'}
    >
      <SidePanel.Header
        title={selectedPerson?.displayName ?? 'Unlinked individuals'}
        titleAdornment={
          suggestionId ? (
            <LocateSuggestionButton
              suggestionID={suggestionId}
              onLocateSuggestionClick={(id) =>
                suggestionDrawerModal.openModal({ suggestionId: id })
              }
              ariaLabel="View source document"
              dataTestId="family-tree-side-panel-view-source-button"
            />
          ) : undefined
        }
        onClose={handleClose}
        onBack={
          (state.sidePanelHistory?.length ?? 0) > 0
            ? handleBackToUnlinked
            : undefined
        }
      />
      <SidePanel.Content>
        <SidePanel.Section>
          {sidePanelVariant === 'linked' && <LinkedPanel />}
          {sidePanelVariant === 'unlinked' && <UnlinkedPanel />}
        </SidePanel.Section>
      </SidePanel.Content>
      <SidePanel.Footer>
        <SidePanelFooter householdId={householdId} />
      </SidePanel.Footer>
      {suggestionDrawerState.isModalOpen && suggestionDrawerState.data && (
        <AIOnboardingSuggestionDrawer
          isOpen={suggestionDrawerState.isModalOpen}
          onClose={() => suggestionDrawerModal.closeModal()}
          suggestionId={suggestionDrawerState.data.suggestionId}
        />
      )}
    </SidePanel.Panel>
  );
}
